import Script from "next/script"
import { useRouter } from "next/router"
import { useEffect, useRef, useState } from "react"
import { Colors } from "../color"
import { H1, H4, Input, VerticalGroup } from "../components"
import Body from "../components/body/body"
import { InnerContainer } from "../components/body/body_style"
import { Shadows } from "../shadows"
import { Form, Label, Option, Select, SmallContainer } from "../styles"
import { TextAreaInput } from "../styles/index"

export enum ContactType {
	Job,
	Press,
	BookBasic,
	BookPremium,
	BookEnterprise,
	Else,
}

const contactStrings = [
	"Job",
	"Press",
	"Book Basic Space",
	"Book Premium Space",
	"Book Enterprise Space",
	"Else",
]

const descriptionStrings = [
	"Want to be part of the SaySom team and bring people together in the digital space? Write us a message!",
	"You want to do an interview with one of our founders, need pictures or other press material? Write us a message!",
	"Just a small step to your personal SaySom Space. Tell us briefly which package you have chosen and what exactly you need. One of our employees will get back to you soon!",
	"Just a small step to your personal SaySom Space. Tell us briefly which package you have chosen and what exactly you need. One of our employees will get back to you soon!",
	"Just a small step to your personal SaySom Space. Tell us briefly which package you have chosen and what exactly you need. One of our employees will get back to you soon!",
	"Have a question? Just want to chat? Don't hesitate, just contact us!",
]

const headerStrings = [
	"Job Request",
	"Press Request",
	"Booking Request",
	"Booking Request",
	"Booking Request",
	"Contact",
]

const turnstileSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY

const Contact = () => {
	const [name, setName] = useState("")
	const [email, setEmail] = useState("")
	const [message, setMessage] = useState("")
	const [contactReason, setContactReason] = useState(ContactType.Else)
	const [premiumPricing, setPremiumPricing] = useState(0)
	const [company, setCompany] = useState("")
	const [turnstileToken, setTurnstileToken] = useState("")

	const turnstileContainer = useRef<HTMLDivElement>(null)
	const turnstileWidgetId = useRef("")

	const router = useRouter()

	useEffect(() => {
		if (router.query) {
			const reason = router.query?.reason
			const pricing = router.query?.pricing

			if (reason) setContactReason(parseInt(reason as string))
			if (pricing) setPremiumPricing(parseInt(pricing as string))
		}
	}, [router, setContactReason, setPremiumPricing])

	const renderTurnstile = () => {
		const turnstile = (window as any).turnstile

		if (
			!turnstileSiteKey ||
			!turnstileContainer.current ||
			!turnstile ||
			turnstileWidgetId.current
		) {
			return
		}

		turnstileWidgetId.current = turnstile.render(turnstileContainer.current, {
			sitekey: turnstileSiteKey,
			callback: (token: string) => setTurnstileToken(token),
			"expired-callback": () => setTurnstileToken(""),
			"error-callback": () => setTurnstileToken(""),
		})
	}

	useEffect(() => {
		renderTurnstile()
	}, [])

	const submit = async (e) => {
		e.preventDefault()

		const submittedMessage =
			premiumPricing !== 0
				? `Premium Pricing: ${premiumPricing}\n\n${message}`
				: message

		try {
			const response = await fetch("/api/mail", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					name,
					email,
					reason: contactStrings[contactReason],
					message: submittedMessage,
					company,
					turnstileToken,
				}),
			})
			const result = await response.json()

			if (!response.ok) {
				throw new Error(result.message ?? "Request failed")
			}

			alert("Thank you for your message!")
			setName("")
			setEmail("")
			setMessage("")
			setCompany("")
		} catch (error) {
			alert(`Error: ${error instanceof Error ? error.message : "Request failed"}`)
		}

		setTurnstileToken("")

		if (turnstileWidgetId.current) {
			;(window as any).turnstile?.reset(turnstileWidgetId.current)
		}
	}

	return (
		<Body
			className="bg-flowy-up dark:bg-flowy-up-dark"
			whiteFooter
			noPadding
			subtitle="Contact"
		>
			<InnerContainer
				style={{ justifyContent: "center", alignItems: "center" }}
			>
				<H1
					style={{
						flex: "0",
						backgroundImage: Colors.flowyText,
						WebkitBackgroundClip: "text",
						WebkitTextFillColor: "transparent",
						whiteSpace: "nowrap",
						alignSelf: "center",
						textAlign: "center",
					}}
				>
					{" "}
					{headerStrings[contactReason]}
				</H1>
				<VerticalGroup style={{ maxWidth: "500px", marginTop: "23px" }}>
					<H4
						style={{
							marginBottom: "24px",
							alignSelf: "center",
							textAlign: "center",
						}}
					>
						{descriptionStrings[contactReason]}
					</H4>
					<SmallContainer
						style={{ flex: 0 }}
						backgroundColor="white"
						shadow={Shadows.highlighted}
					>
						<Form onSubmit={submit}>
							<label
								style={{
									position: "absolute",
									left: "-10000px",
									width: "1px",
									height: "1px",
									overflow: "hidden",
								}}
								aria-hidden="true"
							>
								Company
								<input
									tabIndex={-1}
									autoComplete="off"
									name="company"
									value={company}
									onChange={({ target: { value } }) => setCompany(value)}
								/>
							</label>
							<Label style={{ marginBottom: "20px" }}>
								Name
								<Input
									placeholder="Name"
									type="text"
									value={name}
									onChange={(value) => setName(value)}
								/>
							</Label>
							<Label style={{ marginBottom: "20px" }}>
								Email
								<Input
									placeholder="Email"
									type="email"
									value={email}
									onChange={(value) => setEmail(value)}
								/>
							</Label>
							<Label style={{ marginBottom: "20px" }}>
								Reason
								<Select
									value={contactReason}
									onChange={({ target: { value } }) =>
										setContactReason(parseInt(value))
									}
								>
									{contactStrings.map((v, id) => (
										<Option value={id} key={id}>
											{v}
										</Option>
									))}
								</Select>
							</Label>
							<Label style={{ marginBottom: "20px" }}>
								Message
								<TextAreaInput
									style={{ background: "none" }}
									placeholder="Message"
									value={message}
									onChange={({ target: { value } }) => setMessage(value)}
								/>
							</Label>

							{turnstileSiteKey && (
								<>
									<Script
										src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
										strategy="afterInteractive"
										onLoad={renderTurnstile}
									/>
									<div
										ref={turnstileContainer}
										style={{ alignSelf: "center", minHeight: "65px" }}
									/>
								</>
							)}

							<button
								disabled={
									name.length === 0 ||
									email.length === 0 ||
									message.length === 0 ||
									!email.includes("@") ||
									(Boolean(turnstileSiteKey) && turnstileToken.length === 0)
								}
								type="submit"
								className=" flex self-center  bg-black text-white mt-4 shadow-md rounded-md px-5 py-2 text-sm font-semibold select-none cursor-pointer hover:bg-action transition-colors"
							>
								Send Message
							</button>
						</Form>
					</SmallContainer>
				</VerticalGroup>
			</InnerContainer>
		</Body>
	)
}

export default Contact
